import { useEffect, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { SearchInput } from "@similarweb/ui-components";

import { EAvailabilityMode } from "app/common/services/pageClaims";
import { translate } from "app/filters/i18nFilter";
import { SearchResultsTableWithTrialHook } from "app/pages/sales-intelligence/common-components/search-results-table";
import {
    setFilteredRowsAction,
    setSelectedRowsAction,
} from "app/pages/sales-intelligence/modules/company-analysis/store/competitors/action-creators";
import {
    selectIsCompetitorsRowsSelectionEnabled,
    selectSelectedCompetitorsRowKeys,
} from "app/pages/sales-intelligence/modules/company-analysis/store/competitors/selectors";
import type { CompanyAnalysisCompetititorItem } from "app/pages/sales-intelligence/modules/company-analysis/types/competitors";
import { useIsIndustryTaxonomyOptedIn } from "app/pages/sales-intelligence/modules/industry/hooks/useIsIndustryTaxonomyOptedIn";
import TableActionsPanelCompetitors from "app/pages/sales-intelligence/pages/company-analysis/containers/competitors/TableActionsPanelCompetitors/TableActionsPanelCompetitors";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import { getCompetitorsColumns } from "./columns";
import { CompetitorsFiltersLoader } from "./CompetitorsFiltersLoader";
import { COMPETITORS_TRIAL_HOOK_PROPS } from "./constants/competitorsTrialHookProps";
import { StyledCompetitorsSearchContainer, StyledCompetitorsTableWrapper } from "./styles";
import { useFilteredCompetitors } from "./useFilteredCompetitors";

const getNoData = () => ({
    title: translate("si.company-analysis.company-data.card.empty.header"),
    subtitle: translate("si.company-analysis.company-data.card.empty.text"),
});
const ROW_HEIGHT = 41;

export const CompetitorsContent = ({
    competitors,
    isLoading,
}: {
    competitors?: CompanyAnalysisCompetititorItem[];
    isLoading: boolean;
}) => {
    const dispatch = useDispatch();
    const { filteredCompetitors, handleSearch, currentPage, setCurrentPage } = useFilteredCompetitors(competitors);
    const isRowSelectionEnabled = useSelector(selectIsCompetitorsRowsSelectionEnabled);
    const selectedRowKeys = useSelector(selectSelectedCompetitorsRowKeys);

    // The opt-in alone, not `showsIndustryTaxonomy`: the industry demo is search-only, and the company
    // card already sets this precedent for company analysis.
    const isIndustryTaxonomyOptedIn = useIsIndustryTaxonomyOptedIn();

    const isFeatureHook = salesSettingsHelper.getCompanyCompetitorsFeature() === EAvailabilityMode.HOOK;
    const hookLimit = salesSettingsHelper.getNumCompetitorsHookLimit();

    useEffect(() => {
        dispatch(setFilteredRowsAction(filteredCompetitors));
    }, [filteredCompetitors]);

    const paginationProps = useMemo(
        () => ({
            pageSize: 100,
            total: filteredCompetitors.length,
            current: currentPage,
            onChange(_currentPage: number, _pageSize: number, nextPage: number) {
                setCurrentPage(nextPage);
            },
        }),
        [filteredCompetitors.length, currentPage],
    );

    const selectionProps = useMemo(
        () => ({
            enabled: isRowSelectionEnabled,
            selectedRowKeys,
            preserveSelectedRowKeys: true,
            onChange(_, rows: ReadonlyArray<CompanyAnalysisCompetititorItem & { key: React.Key }>) {
                dispatch(setSelectedRowsAction(rows));
            },
        }),
        [isRowSelectionEnabled, selectedRowKeys],
    );

    const columnConfig = useMemo(() => {
        const columns = getCompetitorsColumns(filteredCompetitors.length, isIndustryTaxonomyOptedIn);

        if (isFeatureHook) {
            return columns.map((column) => ({
                ...column,
                sorter: false,
            }));
        }

        return columns;
    }, [filteredCompetitors.length, isFeatureHook, isIndustryTaxonomyOptedIn]);

    return (
        <>
            {isLoading ? (
                <CompetitorsFiltersLoader />
            ) : (
                <>
                    <StyledCompetitorsSearchContainer>
                        <SearchInput
                            disableClear={true}
                            disabled={isFeatureHook}
                            onChange={handleSearch}
                            placeholder={translate("forms.search.placeholder")}
                        />
                    </StyledCompetitorsSearchContainer>
                    <TableActionsPanelCompetitors isDisabled={filteredCompetitors.length === 0} />
                </>
            )}
            <StyledCompetitorsTableWrapper>
                <SearchResultsTableWithTrialHook
                    withArrowButtons
                    isLoading={isLoading}
                    data={filteredCompetitors}
                    columnConfig={columnConfig}
                    pagination={paginationProps}
                    selection={selectionProps}
                    noData={getNoData()}
                    isTrial={isFeatureHook}
                    trialHookProps={COMPETITORS_TRIAL_HOOK_PROPS}
                    rowHeight={ROW_HEIGHT}
                    trialAvailableRows={hookLimit}
                />
            </StyledCompetitorsTableWrapper>
        </>
    );
};
